import { useState } from "react";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getApiErrorMessage } from "@api/apiError";
import {
    ScheduleTemplateInUseError,
    useArchiveScheduleTemplateMutation,
    useDeleteScheduleTemplateMutation,
    useRestoreScheduleTemplateMutation,
} from "@api/scheduleTemplate.api";

export type ScheduleTemplateLifecycleAction = "archive" | "restore" | "delete";

export interface ScheduleTemplateLifecycleRequest {
    action: ScheduleTemplateLifecycleAction;
    template: ScheduleTemplateListItem;
    /** Set when Delete was refused because the template has applications. */
    isDeleteBlocked?: boolean;
}

interface ScheduleTemplateLifecycleModalsProps {
    request: ScheduleTemplateLifecycleRequest | null;
    onClose: () => void;
    onRequestChange: (request: ScheduleTemplateLifecycleRequest) => void;
}

/**
 * Hub confirms for Archive, Restore and Delete. A refused Delete (409, the
 * template has applications) turns into the blocked-delete confirm whose
 * primary action is Archive (FR-LIFE-043).
 */
export const ScheduleTemplateLifecycleModals = ({
    request,
    onClose,
    onRequestChange,
}: ScheduleTemplateLifecycleModalsProps) => {
    const { toast, error } = useToaster();
    const archiveMutation = useArchiveScheduleTemplateMutation();
    const restoreMutation = useRestoreScheduleTemplateMutation();
    const deleteMutation = useDeleteScheduleTemplateMutation();
    const [displayed, setDisplayed] =
        useState<ScheduleTemplateLifecycleRequest | null>(request);
    if (request && request !== displayed) {
        setDisplayed(request);
    }

    if (!displayed) {
        return null;
    }
    const { template } = displayed;

    // Only the delete mutation suppresses the client's own error toast (its
    // in-use answer is handled here), so only it passes a fallback message;
    // archive and restore failures are already toasted by the client.
    const run = async (options: {
        action: () => Promise<void>;
        successMessage: string;
        fallbackErrorMessage?: string;
    }) => {
        try {
            await options.action();
            toast(options.successMessage, undefined, { variant: "success" });
            onClose();
        } catch (actionError) {
            if (actionError instanceof ScheduleTemplateInUseError) {
                onRequestChange({
                    action: "delete",
                    template,
                    isDeleteBlocked: true,
                });
                return;
            }
            if (options.fallbackErrorMessage) {
                error(
                    getApiErrorMessage(
                        actionError,
                        options.fallbackErrorMessage,
                    ),
                );
            }
        }
    };

    const archive = () =>
        run({
            action: () => archiveMutation.mutateAsync(template.id),
            successMessage: "Policy archived",
        });

    if (displayed.action === "delete" && displayed.isDeleteBlocked) {
        return (
            <ConfirmationModal
                show={request !== null}
                title={`${template.name} is in use`}
                question="This policy has been applied, so it cannot be deleted. Archive it to stop new uses; existing applications keep recognizing."
                yes="Archive"
                no="Back"
                onConfirm={archive}
                onReject={onClose}
                onHide={onClose}
                onExited={() => setDisplayed(null)}
            />
        );
    }
    if (displayed.action === "delete") {
        return (
            <ConfirmationModal
                show={request !== null}
                title={`Delete ${template.name}?`}
                question="This policy has never been applied and will be removed from the hub and every picker."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Back"
                onConfirm={() =>
                    run({
                        action: () => deleteMutation.mutateAsync(template.id),
                        successMessage: "Policy deleted",
                        fallbackErrorMessage: "Failed to delete the policy",
                    })
                }
                onReject={onClose}
                onHide={onClose}
                onExited={() => setDisplayed(null)}
            />
        );
    }
    if (displayed.action === "restore") {
        return (
            <ConfirmationModal
                show={request !== null}
                title={`Restore ${template.name}?`}
                question="The policy will be available again in pickers, rules and bulk Set recognition schedule."
                yes="Restore"
                no="Back"
                onConfirm={() =>
                    run({
                        action: () => restoreMutation.mutateAsync(template.id),
                        successMessage: "Policy restored",
                    })
                }
                onReject={onClose}
                onHide={onClose}
                onExited={() => setDisplayed(null)}
            />
        );
    }
    return (
        <ConfirmationModal
            show={request !== null}
            title={`Archive ${template.name}?`}
            question="It disappears from pickers, rules and bulk Set recognition schedule. Existing schedules keep recognizing, and you can restore it later."
            yes="Archive"
            no="Back"
            onConfirm={archive}
            onReject={onClose}
            onHide={onClose}
            onExited={() => setDisplayed(null)}
        />
    );
};
